import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { apiPortalPricing } from '../../redux/apiSlices/apiSlices';
import { IComparableData, IComparableRow } from '../../pages/CompsViewer/types';

interface ComparablesByNidParams {
  country: string;
  nid: string;
}

interface ComparablesByNidResponse {
  success: boolean;
  results: RawComparableRow[];
}

type ColOrScalar<T> = T | Record<string, T>;

interface RawComparableData {
  latitude:      ColOrScalar<number | null>;
  longitude:     ColOrScalar<number | null>;
  address:       ColOrScalar<string | null>;
  area:          ColOrScalar<number | null>;
  room_num:      ColOrScalar<number | null>;
  banos:         ColOrScalar<number | null>;
  garage:        ColOrScalar<number | null>;
  norm_price:    ColOrScalar<number | null>;
  norm_price_m2: ColOrScalar<number | null>;
  date_create:   ColOrScalar<string | null>;
  source_id:     ColOrScalar<string | null>;
}

type RawComparableRow = Omit<IComparableRow, 'comparable'> & {
  comparable: RawComparableData;
};

const isColumnarField = <T>(val: ColOrScalar<T>): val is Record<string, T> =>
  val !== null && typeof val === 'object' && !Array.isArray(val);

const getCol = <T>(field: ColOrScalar<T>, idx: string): T => {
  if (isColumnarField(field)) {
    return field[idx];
  }
  return field as T; // NOSONAR - TypeScript cannot narrow ColOrScalar<T> to T in the negative branch of a generic type guard
};

const normalizeRows = (raw: RawComparableRow[]): IComparableRow[] => {
  const result: IComparableRow[] = [];

  for (const row of raw) {
    const { comparable, ...rest } = row;
    if (isColumnarField(comparable.latitude)) {
      const indices = Object.keys(comparable.latitude);
      for (const idx of indices) {
        const flatComparable: IComparableData = {
          latitude:      getCol(comparable.latitude,      idx),
          longitude:     getCol(comparable.longitude,     idx),
          address:       getCol(comparable.address,       idx),
          area:          getCol(comparable.area,          idx),
          room_num:      getCol(comparable.room_num,      idx),
          banos:         getCol(comparable.banos,         idx),
          garage:        getCol(comparable.garage,        idx),
          norm_price:    getCol(comparable.norm_price,    idx),
          norm_price_m2: getCol(comparable.norm_price_m2, idx),
          date_create:   getCol(comparable.date_create,   idx),
          source_id:     getCol(comparable.source_id,     idx),
        };
        result.push({ ...rest, comparable: flatComparable });
      }
    } else {
      result.push({ ...rest, comparable: comparable as unknown as IComparableData });
    }
  }

  return result;
};

export const comparablesByNid = apiPortalPricing.injectEndpoints({
  endpoints: builder => ({
    getComparablesByNid: builder.query<IComparableRow[], ComparablesByNidParams>({
      query: ({ country, nid }) =>
        `/comparables_by_nid?country=${country}&nid=${nid}`,
      transformErrorResponse: (error: FetchBaseQueryError) => ({
        errorStatus: error.status,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        messageFromServer: (error.data as any)?.message,
        serviceErrorName: 'getComparablesByNid',
      }),
      transformResponse: (response: ComparablesByNidResponse): IComparableRow[] =>
        normalizeRows(response.results ?? []),
    }),
  }),
});

export const {
  useGetComparablesByNidQuery,
  useLazyGetComparablesByNidQuery,
} = comparablesByNid;
